import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Eye, User, ArrowLeft, Tag } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { API } from "../App";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center" data-testid="post-not-found">
        <h1 className="text-2xl text-white mb-4">Post Not Found</h1>
        <Link to="/blog">
          <Button className="btn-primary">Back to Blog</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background animate-fade-in" data-testid="blog-post-page">
      {/* Hero Image */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img
          src={post.image}
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 -mt-32 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center text-zinc-400 hover:text-white transition-colors mb-6"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Link>

          {/* Header */}
          <header className="mb-8">
            <span className="text-primary font-medium uppercase tracking-wider">
              {post.category}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase tracking-tight mt-2" data-testid="post-title">
              {post.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 mt-6 text-zinc-400">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author_name}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {new Date(post.created_at).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </span>
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                {post.views} views
              </span>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {post.tags.map((tag) => (
                  <span 
                    key={tag} 
                    className="flex items-center gap-1 bg-zinc-900 border border-zinc-800 px-3 py-1 text-sm text-zinc-400"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </header>

          {/* Content */}
          <article 
            className="prose prose-invert prose-lg max-w-none 
              prose-headings:font-heading prose-headings:uppercase prose-headings:tracking-tight
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-white
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-zinc-200
              prose-p:text-zinc-400 prose-p:leading-relaxed
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-ul:text-zinc-400 prose-li:marker:text-primary
              prose-blockquote:border-primary prose-blockquote:text-zinc-300"
            dangerouslySetInnerHTML={{ __html: post.content }}
            data-testid="post-content"
          />

          {/* Share / CTA */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <div className="bg-zinc-900 border border-zinc-800 p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-white uppercase">
                Need These Parts?
              </h3>
              <p className="text-zinc-400 mt-2 mb-6">
                Browse our selection of quality auto parts and accessories.
              </p>
              <Link to="/shop">
                <Button className="btn-primary">Shop Now</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage;
